/**
 * Тема приложения: разрешение предпочтения, применение к DOM и чтение
 * фактических цветов палитры.
 *
 * Единственный источник значений палитры — CSS-переменные в `src/index.css`.
 * Здесь они только читаются: хардкодить хексы второй раз нельзя, иначе
 * хром Telegram, `<meta name="theme-color">` и графики разъедутся с
 * интерфейсом при первой же правке палитры.
 */

export type ThemePreference = 'system' | 'light' | 'dark'
export type ResolvedTheme = 'light' | 'dark'

/**
 * ⚠️ Ключ и формат значения ДУБЛИРУЮТСЯ в инлайн-скрипте `index.html`
 * (анти-вспышка: он обязан отработать до загрузки бандла, поэтому не может
 * импортировать этот модуль). Меняете здесь — правьте и там.
 * Значение хранится плоской строкой `system|light|dark`, а не JSON от
 * zustand/persist: инлайн-скрипту нельзя разбирать чужой формат
 * сериализации, он молча сломается при апгрейде middleware.
 */
export const THEME_STORAGE_KEY = 'app_theme'

export const THEME_PREFERENCES: ThemePreference[] = ['system', 'light', 'dark']

/**
 * Умолчание для того, кто ещё не выбирал тему.
 *
 * ⚠️ Сейчас `light`, а НЕ `system`, и это осознанно. С `system` тёмная тема
 * включилась бы сама у всех франчайзи с тёмной ОС или тёмным клиентом
 * Telegram — то есть ровно тем людям, которые её не просили, и до того, как
 * пройден этап 5 плана (сплошной прогон 99 маршрутов в обеих темах и проверка
 * контраста статусных плашек по WCAG AA). Это тот же приём, что «сначала
 * простановка признака без фильтрации» в контуре CRM: выкатываем механизм,
 * не меняя того, что видит пользователь.
 *
 * Переключатель в шапке доступен всем — тему можно включить руками.
 * После этапа 5 здесь ставится `'system'`, и это единственная нужная правка.
 *
 * ⚠️ Значение ДУБЛИРУЕТСЯ в инлайн-скрипте `index.html` (анти-вспышка).
 */
export const DEFAULT_THEME_PREFERENCE: ThemePreference = 'light'

export function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'system' || value === 'light' || value === 'dark'
}

export function readStoredPreference(): ThemePreference {
  if (typeof window === 'undefined') return DEFAULT_THEME_PREFERENCE
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY)
    return isThemePreference(raw) ? raw : DEFAULT_THEME_PREFERENCE
  } catch {
    // Приватный режим / заблокированное хранилище — тема просто не запомнится.
    return DEFAULT_THEME_PREFERENCE
  }
}

export function writeStoredPreference(preference: ThemePreference): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference)
  } catch {
    // см. readStoredPreference
  }
}

/** Схема, которую просит платформа: Telegram важнее, чем `prefers-color-scheme`. */
export function readSystemScheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'light'

  const telegramScheme = window.Telegram?.WebApp?.colorScheme
  if (telegramScheme === 'dark' || telegramScheme === 'light') {
    return telegramScheme
  }

  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  } catch {
    return 'light'
  }
}

export function resolveTheme(preference: ThemePreference, systemScheme: ResolvedTheme): ResolvedTheme {
  return preference === 'system' ? systemScheme : preference
}

/**
 * Тема ставится атрибутом на `<html>`, а не классом на контейнере: `html`/`body`
 * в `index.css` зафиксированы (`position: fixed`, `overflow: hidden`) и красятся
 * через `@apply bg-background`, поэтому тема обязана стоять выше них — иначе фон
 * вокруг скролл-контейнера останется светлым.
 */
export function applyTheme(resolved: ResolvedTheme): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  if (resolved === 'dark') {
    root.setAttribute('data-theme', 'dark')
  } else {
    root.setAttribute('data-theme', 'light')
  }
}

/* ------------------------------------------------------------------ */
/* Чтение фактических цветов палитры                                   */
/* ------------------------------------------------------------------ */

function clamp255(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)))
}

/** `"45 100% 51.4%"` → `[255, 193, 7]`. Формула из спецификации CSS Color 4. */
export function hslChannelsToRgb(channels: string): [number, number, number] | null {
  const match = channels.trim().match(/^(-?[\d.]+)(?:deg)?\s+([\d.]+)%\s+([\d.]+)%$/)
  if (!match) return null

  const h = parseFloat(match[1])
  const s = parseFloat(match[2]) / 100
  const l = parseFloat(match[3]) / 100
  if (!Number.isFinite(h) || !Number.isFinite(s) || !Number.isFinite(l)) return null

  const a = s * Math.min(l, 1 - l)
  const f = (n: number) => {
    const k = (n + h / 30) % 12
    return l - a * Math.max(-1, Math.min(k - 3, Math.min(9 - k, 1)))
  }
  return [clamp255(f(0) * 255), clamp255(f(8) * 255), clamp255(f(4) * 255)]
}

export function hslChannelsToHex(channels: string): string | null {
  const rgb = hslChannelsToRgb(channels)
  if (!rgb) return null
  return '#' + rgb.map((v) => v.toString(16).padStart(2, '0')).join('')
}

/**
 * Фактическое значение CSS-переменной палитры в виде `#rrggbb`.
 * Возвращает `null`, если переменная не определена — вызывающий код обязан
 * это пережить, а не подставлять свой цвет вслепую.
 */
export function readPaletteHex(variableName: string): string | null {
  if (typeof document === 'undefined') return null
  const raw = getComputedStyle(document.documentElement).getPropertyValue(variableName)
  if (!raw) return null
  return hslChannelsToHex(raw)
}

/** Цвет для `<meta name="theme-color">` и хрома Telegram. */
export function readChromeColors(): { background: string; header: string } {
  const background = readPaletteHex('--background') ?? '#f5f5f5'
  // Шапка приложения красится фоном карточки — так же, как `Header` в вёрстке.
  const header = readPaletteHex('--card') ?? '#ffffff'
  return { background, header }
}
